import type { Card } from '~/db/types'
import type { CardsFrontUnion } from '~/stores/settings'

import { getActiveSnoozeCount } from './card'

const MAX_SNOOZE_LEVEL = 7

type SnoozeCard = Pick<Card, 'snoozeCountSource' | 'snoozeCountLearn'>

const SNOOZE_GAME_SURFACE_CLASSES = [
  'border-orange-200/70 bg-orange-50/95 dark:border-orange-500/25 dark:bg-orange-950/20',
  'border-orange-300/70 bg-orange-100/90 dark:border-orange-500/35 dark:bg-orange-950/30',
  'border-orange-400/65 bg-orange-200/80 dark:border-orange-500/45 dark:bg-orange-950/40',
  'border-orange-500/60 bg-orange-300/70 dark:border-orange-400/50 dark:bg-orange-950/50',
  'border-orange-600/55 bg-orange-400/60 dark:border-orange-400/55 dark:bg-orange-950/60',
  'border-orange-700/50 bg-orange-500/50 dark:border-orange-400/60 dark:bg-orange-950/70',
  'border-orange-800/45 bg-orange-600/45 dark:border-orange-300/65 dark:bg-orange-950/80',
] as const

const SNOOZE_EDIT_CARD_CLASSES = [
  'ring-orange-200/50 bg-orange-50/90 dark:ring-orange-500/25 dark:bg-orange-950/20',
  'ring-orange-300/55 bg-orange-100/85 dark:ring-orange-500/35 dark:bg-orange-950/30',
  'ring-orange-400/60 bg-orange-200/75 dark:ring-orange-500/45 dark:bg-orange-950/40',
  'ring-orange-500/55 bg-orange-300/65 dark:ring-orange-400/50 dark:bg-orange-950/50',
  'ring-orange-600/50 bg-orange-400/55 dark:ring-orange-400/55 dark:bg-orange-950/60',
  'ring-orange-700/45 bg-orange-500/45 dark:ring-orange-400/60 dark:bg-orange-950/70',
  'ring-orange-800/40 bg-orange-600/40 dark:ring-orange-300/65 dark:bg-orange-950/80',
] as const

const SNOOZE_BADGE_CLASSES = [
  'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-200',
  'bg-orange-200 text-orange-800 dark:bg-orange-900/55 dark:text-orange-100',
  'bg-orange-300 text-orange-900 dark:bg-orange-900/65 dark:text-orange-50',
  'bg-orange-400 text-orange-950 dark:bg-orange-800/70 dark:text-orange-50',
  'bg-orange-500 text-white dark:bg-orange-700/75 dark:text-orange-50',
  'bg-orange-600 text-white dark:bg-orange-600/80 dark:text-orange-50',
  'bg-orange-700 text-white dark:bg-orange-500/85 dark:text-white',
] as const

const SNOOZE_BUTTON_CLASSES = [
  'text-orange-700 hover:bg-orange-100/80 hover:text-orange-800 dark:text-orange-200 dark:hover:bg-orange-950/50',
  'text-orange-800 hover:bg-orange-200/80 hover:text-orange-900 dark:text-orange-100 dark:hover:bg-orange-900/55',
  'text-orange-900 hover:bg-orange-300/80 hover:text-orange-950 dark:text-orange-50 dark:hover:bg-orange-900/65',
  'text-orange-950 hover:bg-orange-400/70 hover:text-orange-950 dark:text-orange-50 dark:hover:bg-orange-800/70',
  'text-orange-600 hover:bg-orange-500/25 hover:text-orange-700 dark:text-orange-50 dark:hover:bg-orange-700/75',
  'text-orange-700 hover:bg-orange-600/25 hover:text-orange-800 dark:text-orange-50 dark:hover:bg-orange-600/80',
  'text-orange-800 hover:bg-orange-700/25 hover:text-orange-900 dark:text-white dark:hover:bg-orange-500/85',
] as const

const getSnoozePaletteClassName = (card: SnoozeCard, cardsFront: CardsFrontUnion, palette: readonly string[]) => {
  const level = Math.min(getActiveSnoozeCount(card, cardsFront), MAX_SNOOZE_LEVEL)
  if (!level) return ''

  return palette[level - 1]
}

export const getSnoozeGameSurfaceClassName = (card: SnoozeCard, cardsFront: CardsFrontUnion) =>
  getSnoozePaletteClassName(card, cardsFront, SNOOZE_GAME_SURFACE_CLASSES)

export const getSnoozeEditCardClassName = (card: SnoozeCard, cardsFront: CardsFrontUnion) =>
  getSnoozePaletteClassName(card, cardsFront, SNOOZE_EDIT_CARD_CLASSES)

export const getSnoozeBadgeClassName = (card: SnoozeCard, cardsFront: CardsFrontUnion) =>
  getSnoozePaletteClassName(card, cardsFront, SNOOZE_BADGE_CLASSES)

export const getSnoozeButtonClassName = (card: SnoozeCard, cardsFront: CardsFrontUnion) =>
  getSnoozePaletteClassName(card, cardsFront, SNOOZE_BUTTON_CLASSES)
